
import renderMathInElement from 'katex/dist/contrib/auto-render.mjs';

/**
 * Renders and manages choices for practice questions.
 *
 * Supported templates:
 * - radioList: Single-choice questions
 * - checkboxList: Multiple-choice questions
 * - matchesList: Ordering / matching questions
 *
 * Features:
 * - Supports any number of choices.
 * - Uses choice IDs supplied by the question loader.
 * - Supports text, images and KaTeX expressions.
 * - Supports moving matching choices up and down.
 * - Provides answer collection and visual verification.
 *
 * Expected choice format:
 *
 * {
 *   id: "choice-1",
 *   label: "Using $x^2$",
 *   image: null,
 *   answer: true
 * }
 *
 * Usage:
 *
 * const list = new ChoiceList(
 *   false,
 *   'checkboxList',
 *   question.choices,
 *   true
 * );
 *
 * container.appendChild(list.element);
 *
 * const selectedAnswers = list.answer;
 */
export default class ChoiceList {

  /**
   * @param {boolean} isPracticeMode
   *   When true, preselect choices marked as correct.
   *
   * @param {'radioList'|'checkboxList'|'matchesList'} templateName
   *   Determines the choice UI.
   *
   * @param {Array<Object>} choices
   *   Choices with runtime-generated IDs.
   *
   * @param {boolean} keepOrder
   *   Retained for compatibility with the existing API.
   *   Choice ordering is managed by the caller.
   */
  constructor(isPracticeMode, templateName, choices, keepOrder) {
    this.isPracticeMode = isPracticeMode;
    this.templateName = templateName;
    this.keepOrder = keepOrder;
    this.isMatches = templateName === 'matchesList';

    if (!Array.isArray(choices)) {
      throw new TypeError('ChoiceList expects an array of choices');
    }

    this._element = document.createElement('ul');
    this._element.className = 'list-group';

    // Preserve the existing component reference.
    this._element.container = this;

    switch (templateName) {
      case 'radioList':
        this._renderChoices(choices, 'radio');
        break;

      case 'checkboxList':
        this._renderChoices(choices, 'checkbox');
        break;

      case 'matchesList':
        this._renderMatches(choices);
        break;

      default:
        throw new Error(
          `Unsupported ChoiceList template: ${templateName}`
        );
    }
  }

  /**
   * Renders radio buttons or checkboxes.
   *
   * Each choice gets its own list item.
   * No fixed template size is required.
   *
   * @private
   * @param {Array<Object>} choices
   * @param {'radio'|'checkbox'} type
   */
  _renderChoices(choices, type) {
    // A unique group name prevents radio buttons from
    // different ChoiceList instances interfering.
    const groupName = `choices-${crypto.randomUUID()}`;

    choices.forEach((choice, index) => {
      const li = document.createElement('li');
      li.className = 'list-group-item p-0';

      const label = document.createElement('label');
      label.className =
        'd-flex align-items-center p-2 w-100 m-0';

      const input = document.createElement('input');
      input.className = 'form-check-input me-2';
      input.type = type;

      // IDs are expected to be generated by QuestionLoader.
      const choiceId = String(choice.id ?? index);

      input.value = choiceId;
      input.name = groupName;

      // Use a DOM-unique input ID while preserving the
      // original choice ID in input.value.
      input.id = `${groupName}-${index}`;
      label.htmlFor = input.id;

      const span = document.createElement('span');
      span.className = 'form-check-label w-100';

      this._renderChoiceContent(span, choice);

      if (this.isPracticeMode) {
        input.checked = Boolean(choice.answer);
      }

      label.appendChild(input);
      label.appendChild(span);
      li.appendChild(label);

      this._element.appendChild(li);
    });
  }

  /**
   * Renders a choice's text or image.
   *
   * KaTeX expressions in text are rendered automatically.
   *
   * Text is assigned using textContent to avoid treating
   * question content as executable HTML.
   *
   * @private
   * @param {HTMLElement} container
   * @param {Object} choice
   */
  _renderChoiceContent(container, choice) {
    const text = choice.label ?? choice.choice ?? '';

    if (choice.image) {
      const image = document.createElement('img');

      image.src = choice.image;
      image.className = 'img-fluid';
      image.alt = String(text);

      container.appendChild(image);
    } else {
      container.textContent = String(text);
    }

    renderMathInElement(container, {
      delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '$', right: '$', display: false }
      ],
      throwOnError: false
    });
  }

  /**
   * Renders reorderable matching choices.
   *
   * The up and down controls move the corresponding
   * list item and wrap around at either end.
   *
   * @private
   * @param {Array<Object>} choices
   */
  _renderMatches(choices) {
    choices.forEach(choice => {
      const li = document.createElement('li');

      li.className =
        'list-group-item d-flex align-items-center';

      const controls = document.createElement('span');
      controls.className = 'justify-content-start';

      const upButton = this._createMoveButton(
        'bi-arrow-up',
        'Move up'
      );

      const downButton = this._createMoveButton(
        'bi-arrow-down',
        'Move down'
      );

      controls.appendChild(upButton);
      controls.appendChild(downButton);

      const formCheck = document.createElement('div');
      formCheck.className = 'form-check';

      const label = document.createElement('label');
      label.className = 'form-check-label';

      // Preserve the runtime-generated choice ID.
      label.dataset.id = String(choice.id ?? '');

      this._renderChoiceContent(label, {
        ...choice,
        label: choice.label ?? choice.choice ??
          choice.match ?? ''
      });

      formCheck.appendChild(label);

      li.appendChild(controls);
      li.appendChild(formCheck);

      upButton.addEventListener('click', () => {
        this._moveUp(li);
      });

      downButton.addEventListener('click', () => {
        this._moveDown(li);
      });

      this._element.appendChild(li);
    });
  }

  /**
   * Creates a keyboard-accessible reorder control.
   *
   * @private
   * @param {string} iconClass
   * @param {string} title
   * @returns {HTMLButtonElement}
   */
  _createMoveButton(iconClass, title) {
    const button = document.createElement('button');

    button.type = 'button';
    button.className =
      'btn btn-link btn-sm p-1 text-body';

    button.title = title;
    button.setAttribute('aria-label', title);

    const icon = document.createElement('i');
    icon.className = `bi ${iconClass}`;

    button.appendChild(icon);

    return button;
  }

  /**
   * Moves an item up one position.
   * The first item wraps to the bottom.
   *
   * @private
   * @param {HTMLLIElement} li
   */
  _moveUp(li) {
    const parent = li.parentElement;

    if (li === parent.firstElementChild) {
      parent.appendChild(li);
    } else {
      parent.insertBefore(li, li.previousElementSibling);
    }
  }

  /**
   * Moves an item down one position.
   * The last item wraps to the top.
   *
   * @private
   * @param {HTMLLIElement} li
   */
  _moveDown(li) {
    const parent = li.parentElement;

    if (li === parent.lastElementChild) {
      parent.insertBefore(li, parent.firstElementChild);
    } else {
      parent.insertBefore(li.nextElementSibling, li);
    }
  }

  /**
   * Returns the user's selected answer IDs.
   *
   * For radio and checkbox questions, returns the IDs
   * of checked inputs.
   *
   * For matching questions, returns all choice IDs
   * in their current visual order.
   *
   * @returns {string[]}
   */
  get answer() {
    if (this.isMatches) {
      return Array.from(
        this._element.querySelectorAll('li label'),
        label => label.dataset.id
      );
    }

    return Array.from(
      this._element.querySelectorAll('input:checked'),
      input => input.value
    );
  }

  /**
   * Returns the root DOM element.
   *
   * @returns {HTMLUListElement}
   */
  get element() {
    return this._element;
  }

  /**
   * Clears verification styling.
   *
   * Does not change the selected answers
   * or the current matching order.
   */
  reset() {
    this._element.querySelectorAll('li').forEach(li => {
      li.classList.remove(
        'bg-success',
        'bg-danger',
        'text-white',
        'text-black'
      );
    });
  }

  /**
   * Applies visual verification feedback.
   *
   * For matching questions, highlights every item.
   *
   * For radio and checkbox questions, highlights only
   * the selected choices.
   *
   * @param {boolean} success
   *   Whether the submitted answer is correct.
   */
  verify(success) {
    const successClass = success
      ? 'bg-success'
      : 'bg-danger';

    const failureClass = success
      ? 'bg-danger'
      : 'bg-success';

    this._element.querySelectorAll('li').forEach(li => {
      const shouldHighlight = this.isMatches ||
        Boolean(li.querySelector('input:checked'));

      li.classList.remove(
        successClass,
        failureClass,
        'text-white',
        'text-black'
      );

      if (shouldHighlight) {
        li.classList.add(successClass, 'text-white');
      } else {
        li.classList.add('text-black');
      }
    });
  }
}
